import CallRecoveryAlerts from '../components/CallRecoveryAlerts';
import LsaCalls from '../components/LsaCalls';
import { hiddenPortalViews } from "../services";
import PortalFrame, { PORTAL_VIEWS } from '../components/PortalFrame';
import AppearanceSettings from '../components/AppearanceSettings';
import PortalWelcome from '../components/PortalWelcome';
import PortalPerformance, { periodText } from '../components/PortalPerformance';
import PortalReportUpload from '../components/PortalReportUpload';
import PortalCallUpload from '../components/PortalCallUpload';
import MediaBudget from '../components/MediaBudget';
import InvestmentPlan from '../components/InvestmentPlan';
import PortalIdeas from '../components/WorkIdeas';
import SeoReport from '../components/SeoReport';
import SetupChecklist from '../components/SetupChecklist';
import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import BootSplash from "../components/BootSplash";
import { useAuth } from "../auth";
import { clientSignInPath, passwordRecoveryPath } from "../auth-routing";
import {
  PageHeader,
  EmptyState,
  ErrorState,
  LoadingState,
} from "../components/Ui";
import { LockKeyhole } from "lucide-react";
import {
  DataTable,
  type RecordData,
} from "../components/Workflow";
import Markdown from "../components/Markdown";
import PortalNotifications from "../components/PortalNotifications";
import { AGENCY_TZ } from "../api";
import {
  ChangePasswordDialog,
  NewRequestDialog,
  RequestList,
  RequestOverview,
  type PortalRequest,
} from "../components/PortalRequests";
const PORTAL_PERIODS = ["weekly", "monthly", "last_month", "quarterly", "last_quarter"];
/** Every portal page opens the same way: a title, one plain line, actions on the right. Overview has its greeting. */
const HEADERS: Record<string, { title: string; description: string }> = {
  reports: { title: "Call reports", description: "Your call center's results for each period, with a written summary." },
  calls: { title: "Calls", description: "Your Local Services Ads calls: what happened on each, and who to call back." },
  requests: { title: "Your requests", description: "Ask your agency for a change or for help, and follow each request here." },
  upload: { title: "Appointment report", description: "Upload your appointment export and we send those bookings to Google Ads as conversions." },
  budgeting: { title: "Budget", description: "Your monthly budget by channel, and your investment plan by location." },
  ideas: { title: "Ideas", description: "Share an idea, or paste a chat you had with an AI. We weigh it against your business and talk it through with you." },
  seo: { title: "SEO report", description: "How your local search is moving and the SEO work done each month." },
  setup: { title: "Setup", description: "What is still needed to run everything for you, and who has to do it." },
  customer_match: { title: "Audience tools", description: "Prepare your customer data for more relevant advertising." },
};
// Calls and Ideas draw this header themselves, so their actions (the call-back list, Share idea) sit on its right.
const OWN_HEADER = ["calls", "ideas"];

export default function Portal() {
  const { token } = useParams();
  const [params, setParams] = useSearchParams();
  const requestedView = params.get("view") || "overview";
  const settingsSections = ["account", "appearance", "notifications"];
  const settingsOpen = requestedView === "settings" || settingsSections.includes(requestedView);
  const section = settingsSections.includes(requestedView) ? requestedView : settingsSections.includes(params.get("section") || "") ? params.get("section")! : "account";
  const chosenView = settingsOpen ? section : PORTAL_VIEWS.some(v=>v.id===requestedView) ? requestedView : "overview";
  // Early in a month "this month so far" holds a day or two of spend and no call
  // report yet, so the first week of a month opens on last month instead.
  const agencyDay = Number(new Intl.DateTimeFormat("en-US", { day: "numeric", timeZone: AGENCY_TZ }).format(new Date()));
  const period = PORTAL_PERIODS.includes(params.get("period") || "") ? params.get("period")! : agencyDay <= 7 ? "last_month" : "monthly";
  function setView(next:string) { setParams(p=>{p.set("view",next);p.delete("section");p.delete("request");return p;});setNotice("");setActionError(""); }
  function openRequest(id?: number) { setParams(p => { p.set("view", "requests"); p.delete("section"); if (id) p.set("request", String(id)); else p.delete("request"); return p; }); setNotice(""); setActionError(""); }
  const requestParam = params.get("request") || "";
  const selectedRequestId = /^\d+$/.test(requestParam) && Number.isSafeInteger(Number(requestParam)) && Number(requestParam) > 0 ? Number(requestParam) : null;
  const { user, logout, loading: authLoading, sessionError, retrySession } = useAuth();
  const activeToken = useRef(token);
  activeToken.current = token;
  const reportRequest = useRef<AbortController | null>(null);
  const questionRequest = useRef<AbortController | null>(null);
  const reportElement = useRef<HTMLElement | null>(null);
  const [openingReport, setOpeningReport] = useState<number | null>(null);
  const [session, setSession] = useState<RecordData | null>(null),
    [data, setData] = useState<any>(null),
    [report, setReport] = useState<RecordData | null>(null),
    [error, setError] = useState(""),
    [actionError, setActionError] = useState(""),
    [notice, setNotice] = useState(""),
    [answer, setAnswer] = useState(""),
    [busy, setBusy] = useState(false),
    [loading, setLoading] = useState(true),
    [refresh, setRefresh] = useState(0),
    [sessionRetry, setSessionRetry] = useState(0),
    [requests, setRequests] = useState<PortalRequest[] | null>(null),
    [requestsError, setRequestsError] = useState(""),
    [requestsLoading, setRequestsLoading] = useState(false),
    [requestsTick, setRequestsTick] = useState(0),
    [dialog, setDialog] = useState<"" | "request" | "password">(""),
    [justSent, setJustSent] = useState<number | null>(null),
    // A shared link to an archived client: the server says the portal is closed.
    [closed, setClosed] = useState(false);
  // A section whose service is switched off for this client (services.ts) opens Overview instead.
  const hiddenViews = [...hiddenPortalViews(Array.isArray(session?.services_off) ? (session.services_off as string[]) : []),
    // The report upload shows once the client has the conversion feed or has sent a report.
    ...(session && session.appointment_upload !== true ? ["upload"] : []),
    // Audience tools switched off: an old link opens Overview, like any hidden section (no dead Retry).
    ...(session && !session.tools?.audience ? ["customer_match"] : [])];
  const view = hiddenViews.includes(chosenView) ? "overview" : chosenView;
  async function request(
    path: string,
    body?: RecordData | FormData,
    method = "GET",
    signal?: AbortSignal,
  ) {
    const response = await fetch(path, {
      method,
      signal,
      credentials: token ? "omit" : "include",
      headers: {
        Accept: "application/json",
        "X-NL-AOS-Request": "1",
        ...(token ? { "X-NL-AOS-Portal": token } : {}),
        ...(body && !(body instanceof FormData)
          ? { "Content-Type": "application/json" }
          : {}),
      },
      body: body
        ? body instanceof FormData
          ? body
          : JSON.stringify(body)
        : undefined,
    });
    if (response.status === 401 && !token) window.dispatchEvent(new Event("nl-aos-session-expired"));
    const value = await response.json().catch(() => { throw Error("The server could not complete this request. Please try again."); });
    if (!response.ok) {
      if (token && token === activeToken.current && !signal?.aborted && value.code === "portal_closed") setClosed(true);
      throw Error(value.error || "Request failed");
    }
    return value;
  }
  useEffect(() => {
    if (!token && (!user || authLoading || sessionError)) return;
    let cancelled = false;
    const controller = new AbortController();
    setSession(null);
    setData(null);
    setReport(null);
    setRequests(null);
    setClosed(false);
    setDialog("");
    setNotice("");
    setJustSent(null);
    setActionError("");
    setLoading(true);
    setError("");
    request(
      `/api/portal/session${params.get("client_id") ? `?client_id=${encodeURIComponent(params.get("client_id")!)}` : ""}`,
      undefined, "GET", controller.signal,
    )
      .then((r) => {
        if (!cancelled) setSession(r);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [token, params.get("client_id"), user?.id, authLoading, sessionError, sessionRetry]);
  useEffect(() => {
    reportRequest.current?.abort();
    questionRequest.current?.abort();
    setOpeningReport(null);
    setReport(null);
    setAnswer("");
    setBusy(false);
    return () => { reportRequest.current?.abort(); questionRequest.current?.abort(); };
  }, [session, view]);
  useEffect(() => {
    if (report) {
      reportElement.current?.scrollIntoView({ block: "start", behavior: "auto" });
      reportElement.current?.focus({ preventScroll: true });
    }
  }, [report]);
  useEffect(() => {
    if (!session) return;
    if (["requests","appearance","account","calls","upload","budgeting","ideas","seo","setup"].includes(view)) { setLoading(false);setError("");return; }
    let cancelled = false;
    setData(null);
    setReport(null);
    setAnswer("");
    setLoading(true);
    setError("");
    const cid = session.client.id;
    const endpoint =
      view === "overview"
        ? `/api/performance/client-summary/${cid}?period=${period}`
        : view === "reports"
          ? `/api/callcenter/${cid}/reports`
          : view === "notifications"
            ? `/api/notifications/${cid}/settings`
            : `/api/customermatch/${cid}/uploads`;
    request(endpoint)
      .then((r) => {
        if (!cancelled) setData(r);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [session, view, refresh, period]);
  // The client's requests load on their own, so a failing performance summary
  // never hides them (or the way to send one).
  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    setRequestsLoading(true);
    setRequestsError("");
    request(
      `/api/tasks/client-requests${session.staff_preview ? `?client_id=${session.client.id}` : ""}`,
    )
      .then((r) => {
        if (!cancelled) setRequests(Array.isArray(r) ? r : []);
      })
      .catch((e) => {
        if (!cancelled) setRequestsError(e.message);
      })
      .finally(() => {
        if (!cancelled) setRequestsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [session, requestsTick]);
  // Staff previewing the portal see the client's requests but cannot send one as them.
  const canSend = !!session && !session.staff_preview;
  const clientLogin = user?.role === "client" && !token;
  async function save(
    path: string,
    body: RecordData | FormData,
    method = "POST",
  ) {
    setBusy(true);
    setActionError("");
    setNotice("");
    try {
      const r = await request(path, body, method);
      setNotice("Saved.");
      setRefresh((n) => n + 1);
      return r;
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Could not save");
    } finally {
      setBusy(false);
    }
  }
  if (!token && sessionError) return <div className="full-state"><ErrorState title="Could not check your sign-in" message={sessionError} onRetry={retrySession} /></div>;
  if (!token && authLoading) return <BootSplash />;
  if (!token && !user) return <Navigate to={clientSignInPath(`/portal${params.toString() ? `?${params.toString()}` : ""}`)} replace />;
  if (closed) return (
    <div className="full-state">
      <EmptyState icon={<LockKeyhole aria-hidden="true" />} title="This portal is closed" description="Contact No Limits Marketing if you think this is a mistake." />
    </div>
  );
  return (
    <PortalFrame name={session?.client?.name || "Client portal"} view={view} onView={setView} preview={!!session?.staff_preview} previewClientId={session?.staff_preview && session?.client?.id ? Number(session.client.id) : undefined} audienceEnabled={!!session?.tools?.audience} hiddenViews={hiddenViews} canSend={canSend} onNew={()=>setDialog("request")} onLogout={user&&!token?()=>void logout():undefined} identity={clientLogin?user.email:token?"Secure shared access":"Agency preview"}>
      {dialog === "request" && (
        <NewRequestDialog
          send={request}
          onClose={() => setDialog("")}
          onSent={(r) => {
            setDialog("");
            setJustSent(r?.task_id ?? null);
            setView("requests");
            setNotice("Request sent. You can follow its progress here.");
            setRequestsTick((n) => n + 1);
          }}
        />
      )}
      {dialog === "password" && (
        <ChangePasswordDialog
          send={request}
          onClose={() => setDialog("")}
          onDone={() => {
            setDialog("");
            setNotice("Password changed.");
          }}
        />
      )}
      {session && view === "overview" && <><PortalWelcome name={clientLogin ? user.name : undefined} company={session.client.name}/><RequestOverview rows={requests} loading={requestsLoading} error={requestsError} onOpen={openRequest} onNew={() => setDialog("request")} canSend={canSend} onRetry={() => setRequestsTick(n => n + 1)}/></> }
      {session && settingsOpen && <><PageHeader title="Settings" description="Manage your account, workspace notifications, and personal appearance."/><nav className="portal-settings-nav" aria-label="Settings sections">{[["account","Account & security"],["notifications","Notifications"],["appearance","Appearance"]].map(([id,label])=><button key={id} className={view===id?"active":""} aria-current={view===id?"page":undefined} onClick={()=>{setParams(p=>{p.set("view","settings");p.set("section",id);return p;});setNotice("");setActionError("");}}>{label}</button>)}</nav></>}
      {/* The title shows even while the page loads or fails, so a page never opens on an error alone. */}
      {session && !settingsOpen && HEADERS[view] && !OWN_HEADER.includes(view) && <PageHeader title={HEADERS[view].title} description={HEADERS[view].description} />}
      {notice && (
        <p className="inline-notice" role="status">
          {notice}
        </p>
      )}
      {actionError && <p className="inline-error" role="alert">{actionError}</p>}
      {error ? (
        <ErrorState message={error} onRetry={() => session ? setRefresh((n) => n + 1) : setSessionRetry((n) => n + 1)} />
      ) : loading ? (
        <LoadingState label="Loading client portal" />
      ) : (
        session && (
          <>
            {view === "overview" && <CallRecoveryAlerts clientId={Number(session.client.id)} token={token} />}
            {view === "overview" && session.appointment_upload === true && (
              <p className="inline-notice" role="status">
                <span>Have a new appointment export? Upload it and we send those bookings to Google Ads as conversions.</span>
                <button type="button" className="text-button" onClick={() => setView("upload")}>Upload appointment report</button>
              </p>
            )}
            {view === "overview" && <><section className="portal-results-heading"><div><h2>Performance at a glance</h2></div><div className="portal-period"><label htmlFor="portal-period">Reporting period</label><select id="portal-period" value={period} onChange={e=>setParams(p=>{p.set("period",e.target.value);return p;})}><option value="weekly">This week</option><option value="monthly">This month</option><option value="last_month">Last month</option><option value="quarterly">This quarter</option><option value="last_quarter">Last quarter</option></select><button className="button secondary" onClick={()=>setRefresh(n=>n+1)}>Refresh data</button></div></section>{data&&<PortalPerformance data={data}/>}</>}
            {view === "calls" && <LsaCalls key={session.client.id} clientId={Number(session.client.id)} token={token} header={HEADERS.calls} />}
            {view === "upload" && <PortalReportUpload key={session.client.id} clientId={Number(session.client.id)} send={request} />}
            {view === "budgeting" && <PortalBudget key={session.client.id} clientId={Number(session.client.id)} send={request} />}
            {view === "ideas" && <PortalIdeas key={session.client.id} clientId={Number(session.client.id)} send={request} header={HEADERS.ideas} />}
            {view === "seo" && <SeoReport key={session.client.id} clientId={Number(session.client.id)} send={request} />}
            {view === "setup" && <SetupChecklist key={session.client.id} clientId={Number(session.client.id)} send={request} />}
            {view === "appearance" && <AppearanceSettings/>}
            {view === "account" && <section className="panel workflow-panel portal-account portal-account-details">
              <h2>{clientLogin ? "Your sign-in" : "Workspace access"}</h2>
              <dl className="portal-account-fields"><div><dt>Workspace</dt><dd>{session.client.name}</dd></div>{clientLogin ? <><div><dt>Name</dt><dd>{user.name}</dd></div><div><dt>Sign-in email</dt><dd>{user.email}</dd></div></> : <div><dt>Access</dt><dd>{token ? "Secure shared link" : "Agency preview"}</dd></div>}</dl>
              <section className="portal-account-section" aria-labelledby="portal-access-heading"><h3 id="portal-access-heading">{clientLogin ? "Password & security" : token ? "Sign in with your own account" : "Client account access"}</h3>
                {clientLogin ? <><p>Use your sign-in email for password resets. Ask your agency if you need to change that address.</p><div className="portal-account-actions"><button className="button secondary" onClick={()=>setDialog("password")}>Change password</button><Link className="button secondary" to={passwordRecoveryPath(true, "/portal?view=settings&section=account")}>Reset password by email</Link></div></> : <><p>{token ? "This shared link opens the workspace without a password. For your own sign-in, ask your agency to invite your email address." : "Each client signs in with their own invited email address. You are viewing their workspace as an agency user."}</p>{token && <Link className="button secondary" to="/client-login">Client sign in</Link>}</>}
              </section>
              <section className="portal-account-section" aria-labelledby="portal-email-heading"><h3 id="portal-email-heading">Workspace emails</h3><p>Update emails use the address saved in Notifications. Changing it does not change anyone’s sign-in email or password.</p><button className="button secondary" onClick={() => setView("notifications")}>Manage notifications</button></section>
            </section>}
            {view === "reports" && (
              <>
                {session.tools?.report_upload && <PortalCallUpload key={session.client.id} clientId={Number(session.client.id)} send={request} onDone={(message) => { setNotice(message); setRefresh((n) => n + 1); }} />}
              <section className="panel workflow-panel">
                <DataTable
                  empty={session.tools?.report_upload ? "No call reports yet. Upload your calls above, or your agency adds them." : "No call reports yet. Your agency adds each one here once its period has been analyzed."}
                  rows={(Array.isArray(data) ? data : data?.reports || []).map((r: RecordData) => ({ ...r, period: periodText(r.period_start, r.period_end), total_calls: r.total_calls == null ? null : Number(r.total_calls), scheduled_appointments: r.scheduled_appointments == null ? null : Number(r.scheduled_appointments) }))}
                  columns={[
                    "period",
                    "total_calls",
                    "scheduled_appointments",
                  ]}
                  action={(row) => (
                    <button
                      className="button secondary"
                      disabled={busy || openingReport === Number(row.id)}
                      aria-label={`Read report for ${row.period}`}
                      onClick={async () => {
                        reportRequest.current?.abort();
                        const controller = new AbortController();
                        reportRequest.current = controller;
                        setOpeningReport(Number(row.id));
                        setActionError("");
                        setAnswer("");
                        setReport(null);
                        try {
                          const result = await request(
                            `/api/callcenter/${session.client.id}/reports/${row.id}`,
                            undefined, "GET", controller.signal,
                          );
                          if (!controller.signal.aborted) setReport(result);
                        } catch (e) {
                          if (!controller.signal.aborted) setActionError(
                            e instanceof Error
                              ? e.message
                              : "Report unavailable",
                          );
                        } finally {
                          if (!controller.signal.aborted) setOpeningReport(null);
                        }
                      }}
                    >
                      {openingReport === Number(row.id) ? "Opening…" : "Read report"}
                    </button>
                  )}
                />
                {openingReport !== null && <p role="status">Opening report…</p>}
                {report && (
                  <article className="portal-report" ref={reportElement} tabIndex={-1} aria-label="Report summary">
                    <div className="toolbar"><h2>Report summary</h2><button className="button secondary" disabled={busy} onClick={() => { reportRequest.current?.abort(); setReport(null); setAnswer(""); }}>Close report</button></div>
                    {report.period && <p className="workflow-note">{periodText(report.period.start, report.period.end)}</p>}
                    <Markdown>
                      {report.summary ||
                        report.client_summary ||
                        (report.narrative_status === "failed"
                          ? "The written summary could not be prepared. Contact your agency team for help."
                          : "The written summary is still being prepared. Reopen this report in a moment to check again.")}
                    </Markdown>
                    {session.tools?.report_questions && <form
                      className="operation-form"
                      onSubmit={async (e) => {
                        e.preventDefault();
                        if (busy) return;
                        const controller = new AbortController();
                        questionRequest.current?.abort();
                        questionRequest.current = controller;
                        setBusy(true);
                        setActionError("");
                        setAnswer("");
                        try {
                          const r = await request(
                            `/api/callcenter/${session.client.id}/reports/${report.report_id}/ask`,
                            {
                              question: String(
                                new FormData(e.currentTarget).get("question"),
                              ),
                            },
                            "POST",
                            controller.signal,
                          );
                          if (!controller.signal.aborted) setAnswer(r.answer || "No answer returned.");
                        } catch (e) {
                          if (!controller.signal.aborted) setActionError(
                            e instanceof Error ? e.message : "Question failed",
                          );
                        } finally {
                          if (!controller.signal.aborted) setBusy(false);
                        }
                      }}
                    >
                      <label>
                        Ask about this report
                        <textarea
                          name="question"
                          required
                          maxLength={1000}
                          rows={3}
                        />
                      </label>
                      <button className="button secondary" disabled={busy}>
                        {busy ? "Preparing answer…" : "Ask report"}
                      </button>
                    </form>}
                    {answer && <Markdown>{answer}</Markdown>}
                  </article>
                )}
              </section>
              </>
            )}
            {view === "notifications" && data && (
              <PortalNotifications
                key={session.client.id}
                data={data}
                busy={busy}
                onSave={(fields) => save(`/api/notifications/${session.client.id}/settings`, fields, "PUT")}
              />
            )}
            {view === "customer_match" && (
              <section className="panel workflow-panel">
                <p className="workflow-note">
                  Prepare a hashed customer list for manual upload to Google
                  Ads. This does not upload contacts to an advertising account.
                </p>
                <form
                  className="operation-form"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const form = new FormData(e.currentTarget);
                    const file = form.get("appointment_csv") as File;
                    if (file.size > 4 * 1024 * 1024) {
                      setActionError("Choose a CSV under 4 MB.");
                      return;
                    }
                    const r = await save(
                      `/api/customermatch/${session.client.id}/upload`,
                      form,
                    );
                    if (r?.hashed_csv_base64) {
                      const a = document.createElement("a");
                      a.href = URL.createObjectURL(
                        new Blob([atob(r.hashed_csv_base64)], {
                          type: "text/csv",
                        }),
                      );
                      a.download = "customer-match.csv";
                      a.click();
                      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
                    }
                  }}
                >
                  <label>
                    Customer CSV
                    <input
                      type="file"
                      name="appointment_csv"
                      accept=".csv,text/csv"
                      required
                    />
                  </label>
                  <label>
                    Audience name
                    <input name="google_ads_list_name" />
                  </label>
                  <button className="button" disabled={busy}>
                    Prepare and download CSV
                  </button>
                </form>
                <DataTable
                  rows={Array.isArray(data) ? data : []}
                  columns={[
                    "original_filename",
                    "hashed_row_count",
                    "upload_status",
                    "processed_at",
                  ]}
                />
              </section>
            )}
          </>
        )
      )}
      {session && view === "requests" && (
        <RequestList
          rows={requests}
          loading={requestsLoading}
          error={requestsError}
          onRetry={() => setRequestsTick((n) => n + 1)}
          canSend={canSend}
          highlight={justSent}
          selectedRequestId={selectedRequestId}
          onSelectRequest={id => { setParams(p => { if (id) p.set("request", String(id)); else p.delete("request"); return p; }); }}
        />
      )}
    </PortalFrame>
  );
}

/** Budget in the portal: the channel planner, or the investment plan by tactic and location.
 * Its two views are tabs like Settings' tabs, not a segmented track stretched across the page. */
function PortalBudget({ clientId, send }: { clientId: number; send: Parameters<typeof MediaBudget>[0]['send'] }) {
  const [mode, setMode] = useState<'channels' | 'plan'>('channels');
  return <>
    <nav className="portal-settings-nav" aria-label="Budget views">
      {([['channels', 'Channels'], ['plan', 'Investment plan']] as const).map(([id, title]) => <button key={id} type="button" className={mode === id ? 'active' : ''} aria-pressed={mode === id} onClick={() => setMode(id)}>{title}</button>)}
    </nav>
    {mode === 'plan' ? <InvestmentPlan clientId={clientId} send={send} /> : <MediaBudget clientId={clientId} send={send} />}
  </>;
}
